// Components
import React, { useEffect, useRef } from 'react';

import Button from '@amzn/awsui-components-react/polaris/button';
import ButtonDropdown from '@amzn/awsui-components-react/polaris/button-dropdown';
import SpaceBetween from '@amzn/awsui-components-react/polaris/space-between';
import { ComplexSignupDialog } from '../complexSignup/signupDialog';
import { TimeModal } from './timeModal';
import { publishKatalMetric } from '../katalAnalytics';
// Hooks
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useCreateSignup,
  useDeleteSignup,
  useLatestSignupMutationState,
} from '../../hooks/shifts/shiftSignup';
import { useUser } from '../../hooks/user';
import { featureIsEnabled, useFeatures } from '../../hooks/features';
import { useNavigate, useSearchParams } from 'react-router-dom';
// Types
import { Attendance, AttendanceStatus, Event, Shift, Signup } from '@amzn/red-velvet-api';
// Utils
import { useAttendance, useTrackTime } from '../../hooks/attendance/attendanceMutation';
import { EVENT_CREATION, MANAGE_SIGNUPS_ON_EVENT_LEVEL } from '../../utils/constants';

interface ActionLinkProps {
  event: Event;
  shift: Shift;
  signup?: Signup;
  attendance?: Attendance;
  forceCancelSignup?: boolean;
}

export function ShiftActions(props: ActionLinkProps) {
  const { event, shift, forceCancelSignup, signup, attendance } = props;

  const [modalVisible, setModalVisible] = useState(false);
  const shiftStatus = useLatestSignupMutationState(event.eventId, shift.shiftId, signup);

  const hasUserParticipated = attendance !== undefined || shiftStatus.signup !== undefined;

  if (hasUserParticipated && shift.complete) {
    return (
      <TrackPastData
        event={event}
        shift={shift}
        signup={shiftStatus.signup}
        attendance={attendance}
        startComplexSignup={() => setModalVisible(true)}
      />
    );
  }

  return (
    <>
      <ToggleSignup
        event={event}
        shift={shift}
        signup={shiftStatus.signup}
        startComplexSignup={() => setModalVisible(true)}
        forceCancelSignup={forceCancelSignup ?? false}
      />
      <ComplexSignupDialog
        event={event}
        shift={shift}
        visible={modalVisible}
        onDismiss={() => setModalVisible(false)}
      />
    </>
  );
}

export function requiresComplexSignup(event: Event, includeGuestsCheck = true) {
  return (
    (includeGuestsCheck && event.allowGuests) ||
    (event.questions && event.questions.length > 0) ||
    (event.waiver && event.waiver.acceptance !== 'none' && event.waiver.url) ||
    event.hasTShirt
  );
}

export function EditEventLink({ event }: { event: Event }) {
  const { t } = useTranslation();
  const features = useFeatures();
  const [searchParams] = useSearchParams();
  const isVcCreated = featureIsEnabled(EVENT_CREATION, features, searchParams) && event.vcCreated;

  const linkText = isVcCreated
    ? 'eventDetails.shiftDisplay.edit'
    : 'eventDetails.shiftDisplay.editEventBenevity';

  const linkUrl = isVcCreated
    ? `/create-event?eventId=${event.eventId}`
    : `https://amazon.benevity.org/volunteer/${event.eventId}/edit`;

  const analyticsName = isVcCreated ? 'editEventInVCLink' : 'editEventInBenevityLink';

  return (
    <Button
      href={linkUrl}
      data-aci-analytics-name={analyticsName}
      target="_blank"
      external={!isVcCreated}
    >
      {t(linkText)}
    </Button>
  );
}

export function ManageEventButton({ event, shifts }: { event: Event; shifts: Shift[] }) {
  const namespaces = ['activity', 'translation'];
  const { t } = useTranslation(namespaces);
  const navigate = useNavigate();
  const features = useFeatures();
  const [searchParams] = useSearchParams();

  const isEventCreationEnabled = featureIsEnabled(EVENT_CREATION, features, searchParams);
  const isManageSignupsEventLevelEnabled = featureIsEnabled(
    MANAGE_SIGNUPS_ON_EVENT_LEVEL,
    features,
    searchParams
  );

  // If the event's required fields: Primary Activity and Focus Area fields have not been completed,
  // we need to open the Step 1: Event Detail  (0-indexed) first instead of Step 2: Track Attendance
  const wizardPageToNavigate = event.primaryActivity && event.focusArea ? 1 : 0;

  return (
    <>
      <ButtonDropdown
        items={[
          {
            id: 'trackAttendanceEventLevel',
            text: t('coordinatorEventShiftsTable.actions.bulkUpload.trackAttendanceEventLevel'),
          },
          {
            id: 'editEvent',
            text: isEventCreationEnabled
              ? t('coordinatorEventShiftsTable.actions.editEvent')
              : t('eventDetails.shiftDisplay.editEventBenevity'),
            href: isEventCreationEnabled
              ? `/create-event?eventId=${event.eventId}`
              : `https://amazon.benevity.org/volunteer/${event.eventId}/edit`,
            external: !isEventCreationEnabled,
          },
          {
            id: 'manageSignups',
            text: t('coordinatorEventShiftsTable.actions.manageSignups'),
            href: `/manage-signups?eventId=${event.eventId}`,
            disabled: !isManageSignupsEventLevelEnabled,
            disabledReason: t('coordinatorEventShiftsTable.actions.manageSignupsDisabled'),
          },
        ]}
        onItemClick={({ detail }) => {
          if (detail.id === 'trackAttendanceEventLevel') {
            publishKatalMetric('attendance_bulkUploadEventLevelTrackingWizardOpened', '');
            navigate(`/track-attendance?eventId=${event.eventId}&page=${wizardPageToNavigate}`);
          }
        }}
      >
        {t('coordinatorEventShiftsTable.actions.eventActionsButton')}
      </ButtonDropdown>
    </>
  );
}

type ActionInput = {
  event: Event;
  shift: Shift;
  signup: Signup | undefined;
  attendance?: Attendance;
  startComplexSignup: () => void;
  myActivityEnabled?: boolean;
  forceCancelSignup?: boolean;
};

export function TrackPastData({
  event,
  shift,
  signup,
  attendance,
  startComplexSignup,
}: ActionInput) {
  return (
    <div>
      <SpaceBetween direction="horizontal" size="s">
        <TrackAttendance
          event={event}
          shift={shift}
          signup={signup}
          attendance={attendance}
          startComplexSignup={startComplexSignup}
        />
      </SpaceBetween>
    </div>
  );
}

export function TrackAttendance({ event, shift }: ActionInput) {
  const { t } = useTranslation();
  const [modalVisible, setModalVisible] = useState(false);
  const timeMutation = useTrackTime(shift.eventId, shift.shiftId);
  const user = useUser();
  const attendance = useAttendance(event.eventId, shift.shiftId, user.userId);

  return (
    <>
      <SpaceBetween direction="horizontal" size="s">
        {attendance.data?.attendanceStatus !== 'DID_NOT_ATTEND' ? (
          <Button
            fullWidth
            loading={timeMutation.isPending}
            data-testid="attendace.didNotAttendButton"
            data-aci-analytics-name="shifts_didNotAttend"
            onClick={() =>
              timeMutation.mutate({
                eventId: shift.eventId,
                shiftId: shift.shiftId,
                alias: user.userId,
                minutes: 0,
                attendanceStatus: AttendanceStatus.DID_NOT_ATTEND,
              })
            }
          >
            {t('eventDetails.shiftDisplay.didNotAttend')}
          </Button>
        ) : null}
        <Button
          fullWidth
          variant={attendance.data ? undefined : 'primary'}
          data-aci-analytics-name="shifts_attendanceDialog"
          onClick={() => setModalVisible(true)}
        >
          {attendance.data && attendance.data.attendanceStatus === 'ATTENDED'
            ? t('eventDetails.shiftDisplay.editTime')
            : t('eventDetails.shiftDisplay.trackTime')}
        </Button>
      </SpaceBetween>
      <TimeModal shifts={[shift]} visible={modalVisible} onDismiss={() => setModalVisible(false)} />
    </>
  );
}

// Although this would be more readable as two separate components (one for delete and one for signup)
// this causes problems with the accessibility for Complex signups.  When they are one component, the
// Button that was focused when a complex signup dialog is opened is still there when the dialog is closed
// so the keyboard focus automatically returns to it.  If they are separate components, the focus is lost
// when the dialog is closed, which means keyboard users have to re-navigate through the whole page
export function ToggleSignup({
  event,
  shift,
  signup,
  startComplexSignup,
  forceCancelSignup,
}: ActionInput) {
  const { t } = useTranslation();
  const signupMutation = useCreateSignup(event.eventId, shift.shiftId);
  const deleteMutation = useDeleteSignup(event.eventId, shift.shiftId);
  const user = useUser();
  const hasCancelled = useRef(false);

  const isDelete = signup !== undefined;
  const isComplexShiftSignup = requiresComplexSignup(event);
  // TODO: Enforce metrics in integ test: https://issues.amazon.com/issues/cce-3970
  const signup_metric = isComplexShiftSignup ? 'complex_signup_dialog' : 'shifts_signup';

  // Auto-trigger delete mutation if one-click cancel signup is found.
  useEffect(() => {
    if (forceCancelSignup && isDelete && !shift.complete && signup && !hasCancelled.current) {
      hasCancelled.current = true;

      // GOLDEN PATH NOTE
      // Defer the mutation call until after the current render cycle completes to allow
      // React Query to properly attach to the component context
      queueMicrotask(() => {
        deleteMutation.mutate({
          eventId: shift.eventId,
          shift,
          request: { signupId: signup.signupId },
        });
      });
    }
  }, [forceCancelSignup, isDelete, signup, shift, deleteMutation]);

  return (
    <Button
      fullWidth
      loading={signupMutation.isPending || deleteMutation.isPending}
      variant={!isDelete ? 'primary' : undefined}
      // TODO: Fix this properly https://issues.amazon.com/issues/cce-4091
      data-testid={
        isComplexShiftSignup ? 'complexSignup.registerButton' : 'simpleSignup.registerButton'
      }
      data-aci-analytics-name={!isDelete ? signup_metric : `shifts_cancelDetails`}
      onClick={() => {
        if (isDelete) {
          deleteMutation.mutate({
            eventId: shift.eventId,
            shift,
            request: { signupId: signup.signupId },
          });
        } else {
          if (isComplexShiftSignup) {
            startComplexSignup();
          } else {
            signupMutation.mutate({
              eventId: shift.eventId,
              shift: shift,
              request: { shift: shift.shiftId, alias: user.userId },
            });
          }
        }
      }}
    >
      {!isDelete
        ? t('eventDetails.shiftDisplay.register')
        : !shift.complete
          ? t('eventDetails.shiftDisplay.cancel')
          : t('eventDetails.shiftDisplay.didNotAttend')}
    </Button>
  );
}
